import { TranslateService } from '@ngx-translate/core';
import { translated } from './translate-utils';

/**
 * Returns a human-readable string representing how long ago a given date was
 * relative to now (e.g. "Just now", "3m ago", "3h ago", "2d ago").
 *
 * Accepts an optional `TranslateService`. When provided, translation keys under
 * `TimeSince.*` are used (falling back to `en` when the active language has no
 * translation). When omitted the function falls back to plain English strings.
 */
const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

export function timeSinceNow(date: Date | string, translate?: TranslateService): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const diffMs = Date.now() - dateObj.getTime();
    if (Number.isNaN(diffMs)) return '—';

    if (diffMs < MS_PER_MINUTE)
        return translateTimeSince(translate, 'JustNow', undefined, 'Just now');
    if (diffMs < MS_PER_HOUR) {
        const count = Math.floor(diffMs / MS_PER_MINUTE);
        return translateTimeSince(translate, 'MinutesAgo', { count }, `${count}m ago`);
    }
    if (diffMs < MS_PER_DAY) {
        const count = Math.floor(diffMs / MS_PER_HOUR);
        return translateTimeSince(translate, 'HoursAgo', { count }, `${count}h ago`);
    }
    const count = Math.floor(diffMs / MS_PER_DAY);
    return translateTimeSince(translate, 'DaysAgo', { count }, `${count}d ago`);
}

function translateTimeSince(
    translate: TranslateService | undefined,
    key: 'JustNow' | 'MinutesAgo' | 'HoursAgo' | 'DaysAgo',
    params?: Readonly<Record<string, unknown>>,
    fallback?: string
): string {
    if (!translate) return fallback ?? key;

    const translationKey = `TimeSince.${key}`;
    const text = translated(translate, translationKey, params);

    return text === translationKey ? (fallback ?? key) : text;
}

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

/** Returns a formatter for a medium date and a short time, for example "Sep 30, 2026, 2:15 PM" in English. */
export function getDateTimeFormatter(language: string | undefined): Intl.DateTimeFormat {
    const locale = language || 'en';
    let formatter = dateTimeFormatters.get(locale);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
        dateTimeFormatters.set(locale, formatter);
    }
    return formatter;
}
